import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { languageNames, type SupportedLanguage, supportedLanguages } from '@/i18n/config';
import { CONTROL_HEIGHT, CONTROL_SQUARE } from '@/components/ui/control-size';
import { cn } from '@/lib/utils';

/**
 * Language Toggle Component
 *
 * Displays a dropdown to switch between supported languages (English, Ukrainian, Russian).
 * Uses i18next for language management with localStorage persistence.
 *
 * Features:
 * - Auto-detection from browser language
 * - Manual language selection
 * - Persists user preference to localStorage
 * - Shows current language in tooltip
 *
 * @example
 * ```tsx
 * <LanguageToggle />
 * ```
 */
interface LanguageToggleProps {
  buttonClassName?: string;
  iconClassName?: string;
  /**
   * Show the current language name (+ chevron) next to the globe instead of an
   * icon-only button. Used on the sign-in and onboarding screens, where it must
   * be obvious this control switches languages. Defaults to icon-only (sidebar,
   * embedded controls).
   */
  showLabel?: boolean;
}

export function LanguageToggle({
  buttonClassName,
  iconClassName,
  showLabel = false,
}: LanguageToggleProps = {}) {
  const { t, i18n } = useTranslation();

  const handleLanguageChange = (language: SupportedLanguage) => {
    void i18n.changeLanguage(language); // Explicitly ignore promise - language change is fire-and-forget
  };

  const currentLanguage = i18n.language as SupportedLanguage;
  const currentLanguageName = languageNames[currentLanguage] || languageNames.en;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={
            buttonClassName ??
            (showLabel
              ? cn(
                  CONTROL_HEIGHT.md,
                  'inline-flex items-center gap-2 whitespace-nowrap rounded-button border border-border bg-bg-elevated px-3 text-sm font-medium text-fg-muted transition-colors duration-150 hover:bg-bg-surface hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )
              : cn(
                  CONTROL_SQUARE.md,
                  'rounded-button p-2 text-fg-muted transition-all duration-150 hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                ))
          }
          aria-label={t('common:common.currentLanguage', {
            language: currentLanguageName,
          })}
          title={currentLanguageName}
        >
          <Globe
            className={iconClassName ?? (showLabel ? 'h-4 w-4' : 'h-5 w-5')}
            aria-hidden="true"
          />
          {showLabel && <span>{currentLanguageName}</span>}
          {showLabel && (
            <ChevronDown className="h-4 w-4 text-fg-subtle" aria-hidden="true" />
          )}
          <span className="sr-only">{t('common:common.selectLanguage')}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44 p-1">
        {supportedLanguages.map((language) => {
          const isSelected = currentLanguage === language;
          return (
            <DropdownMenuItem
              key={language}
              onClick={() => handleLanguageChange(language)}
              className={cn(
                'flex items-center justify-between gap-3',
                isSelected && 'bg-primary-dim text-primary-fg'
              )}
            >
              <span className={cn(isSelected && 'font-medium')}>
                {languageNames[language]}
              </span>
              {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
            </DropdownMenuItem>
          );
        })}
        {/* This switches ONLY the interface language — analysis and report
            language are separate, project-level settings. Without this line
            the control reads as if it governed both. */}
        <p className="border-t border-border px-2 pt-1.5 pb-1 text-xs text-fg-subtle">
          {t('common:common.interfaceLanguageOnly')}
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
